import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { useIsMobile } from '@/hooks/use-mobile';
import { Time } from '@/lib/db-client';
import { setUsers, subscribeUsers, type UsersResponse } from '@/lib/collections/users';
import { cn } from '@/lib/utils';
import { useAuth } from '@pooflabs/web';
import { Building2, Recycle, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const CITIES = ['Lagos', 'Abuja', 'Ibadan'] as const;

const roleOptions = [
  {
    value: 'household',
    label: 'Household',
    description: 'I create recovery listings',
    icon: Recycle,
  },
  {
    value: 'company',
    label: 'Recycling Company',
    description: 'I recover & process plastic',
    icon: Building2,
  },
  {
    value: 'agent',
    label: 'Pickup Agent',
    description: 'I run community drives',
    icon: Truck,
  },
] as const;

const onboardingSchema = z.object({
  role: z.enum(['household', 'company', 'agent'], {
    message: 'Choose a role to continue',
  }),
  displayName: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(40, 'Name is too long'),
  city: z.enum(CITIES, { message: 'Choose your city' }),
});

interface FormState {
  role: '' | 'household' | 'company' | 'agent';
  displayName: string;
  city: '' | (typeof CITIES)[number];
}

const initialState: FormState = { role: '', displayName: '', city: '' };

/**
 * Global first-time-onboarding gate. Mount once at the app root. Watches the
 * connected wallet; if `users/{address}` doesn't exist yet, opens a glass
 * modal (Dialog on desktop, bottom Sheet on mobile) asking for role/name/city.
 */
export function OnboardingGate() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const { data: profile, loading } = useRealtimeData<UsersResponse | null>(
    subscribeUsers,
    !!user?.address,
    user?.address ?? ''
  );

  useEffect(() => {
    if (!user?.address) {
      setOpen(false);
      return;
    }
    if (!loading && profile === null) {
      setOpen(true);
    }
    if (profile) {
      setOpen(false);
    }
  }, [user?.address, loading, profile]);

  const handleSubmit = async () => {
    const result = onboardingSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        fieldErrors[String(issue.path[0])] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    if (!user?.address) return;

    setSubmitting(true);
    setErrors({});
    try {
      const success = await setUsers(user.address, {
        role: result.data.role,
        displayName: result.data.displayName,
        city: result.data.city,
        createdAt: Time.Now,
      });
      if (success) {
        toast.success(`Welcome to TrashLink, ${result.data.displayName}!`);
        setOpen(false);
        setForm(initialState);
      } else {
        toast.error('Could not save your profile. Please try again.');
      }
    } catch (err) {
      console.error('Onboarding submit failed', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formBody = (
    <div className='space-y-6'>
      <div>
        <Label className='text-xs uppercase tracking-wide text-muted-foreground'>
          I am a...
        </Label>
        <div className='mt-3 grid gap-3'>
          {roleOptions.map((opt) => {
            const Icon = opt.icon;
            const active = form.role === opt.value;
            return (
              <button
                key={opt.value}
                type='button'
                onClick={() => setForm((f) => ({ ...f, role: opt.value }))}
                className={cn(
                  'flex items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200',
                  active
                    ? 'border-primary/60 bg-gradient-to-r from-primary/10 to-accent/10 shadow-[0_0_24px_-8px_hsl(var(--primary)/0.5)]'
                    : 'border-border/60 bg-secondary/30 hover:border-border'
                )}
              >
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                    active ? 'bg-gradient-to-br from-primary to-accent' : 'bg-muted'
                  )}
                >
                  <Icon
                    className={cn('h-5 w-5', active ? 'text-background' : 'text-muted-foreground')}
                    strokeWidth={2}
                  />
                </span>
                <span>
                  <span className='block text-sm font-semibold text-foreground'>{opt.label}</span>
                  <span className='block text-xs text-muted-foreground'>{opt.description}</span>
                </span>
              </button>
            );
          })}
        </div>
        {errors.role && <p className='mt-2 text-xs text-destructive'>{errors.role}</p>}
      </div>

      <div>
        <Label htmlFor='displayName' className='text-xs uppercase tracking-wide text-muted-foreground'>
          Display name
        </Label>
        <Input
          id='displayName'
          placeholder='e.g. Amaka O.'
          value={form.displayName}
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          className='mt-2'
        />
        {errors.displayName && <p className='mt-2 text-xs text-destructive'>{errors.displayName}</p>}
      </div>

      <div>
        <Label className='text-xs uppercase tracking-wide text-muted-foreground'>City</Label>
        <Select
          value={form.city}
          onValueChange={(value) => setForm((f) => ({ ...f, city: value as FormState['city'] }))}
        >
          <SelectTrigger className='mt-2'>
            <SelectValue placeholder='Select your city' />
          </SelectTrigger>
          <SelectContent>
            {CITIES.map((city) => (
              <SelectItem key={city} value={city}>
                {city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.city && <p className='mt-2 text-xs text-destructive'>{errors.city}</p>}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className='w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90'
        size='lg'
      >
        {submitting ? 'Joining the network...' : 'Join the recovery network'}
      </Button>
    </div>
  );

  if (!user?.address) return null;

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side='bottom' className='max-h-[90vh] overflow-y-auto rounded-t-3xl border-border/60 bg-card/95 backdrop-blur-xl'>
          <SheetHeader className='text-left'>
            <SheetTitle className='font-heading text-xl'>Join TrashLink</SheetTitle>
            <SheetDescription>Tell us who you are so the network knows how to route you.</SheetDescription>
          </SheetHeader>
          <div className='mt-5'>{formBody}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='border-border/60 bg-card/95 backdrop-blur-xl sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='font-heading text-2xl'>Join TrashLink</DialogTitle>
          <DialogDescription>Tell us who you are so the network knows how to route you.</DialogDescription>
        </DialogHeader>
        <div className='mt-2'>{formBody}</div>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingGate;
